import { LoggingService } from "../sdServices/loggingSvc";
import { WindowCommunicationBridgeService } from "../../ShellCommon/eventModule/window-communication-bridge";
import {
  reauthorizationCompletedEventName,
  reauthorizationInProgressLocalStorageKeyName,
  reauthVerifyLoginQueryString
} from "../sdServices/signInReauthorizationService";
import { msalErrorForLoginRequired } from "../sdServices/authentication/authentication-logger";
import { MsalAuthenticationServiceImplementation } from "../sdServices/msal/msal-authentication-service";

export const alreadyReauthenticatedQueryStringParameterName = "triedReauthenticating";
export const errorQueryStringParameterName = "error";

// This controller serves the /reauth state and should be launched in a new window.
//  Basic flow here is:
//   1. Different window (e.g. main service desk window) determines we need to reauthenticate.
//   2. Launches a new window with the reauthentication state as the target (which hosts this controller).
//   3. The first time this controller is used in this new window, we determine if we have a known failure state and auto-login.
//      3.1. No known failure state requires the client to manually click a "login" button.
//   4. After login, the new window navigates back to the same reauth state, but this time it has a QSP set.
//   5. If we are healthy, login completes and the window closes.
//   6. Not healthy = User sees a sad face and should have an errorState -- leading to manual reauth or escalation.
//  Note that the controller is currently suffering from some name confusion and thinks
//  it's "Reauthorizing" when it is really "Reauthenticating".
export class SignInReauthorizationController {
  public accountName: string;
  public errorState: string;
  public showFallbackMessage: boolean;

  // @ngInject
  constructor(
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private loggingService: LoggingService,
    private $location: ng.ILocationService,
    windowCommunicationBridgeService: WindowCommunicationBridgeService,
    $timeout: ng.ITimeoutService,
    private $rootScope: ng.IRootScopeService,
    private $state: ng.ui.IStateService,
    private $window: ng.IWindowService
  ) {
    this.accountName = msalAuthenticationService.userInfo.idToken.preferred_username;
    this.errorState = msalAuthenticationService.userInfo.loginError;
    this.showFallbackMessage = false;
    let closeIfAuthenticated = false;

    // Allow callers to launch this page in a way that would self-close.
    let foundKey = Object.keys(this.$state.params).find(
      (x) => x.toLowerCase() == reauthVerifyLoginQueryString.toLowerCase()
    );
    if (
      foundKey &&
      (this.$state.params[foundKey] === undefined || this.$state.params[foundKey].toLowerCase() == "true")
    ) {
      closeIfAuthenticated = true;
    }

    if (this.errorState) {
      // If we have an error state, we know a login error occurred and will allow manual reauthentication.
      this.loggingService.logInfo(`Reauthentication beginning with error state: ${this.errorState}`);
    } else if (this.isLoginRequired()) {
      this.loggingService.logInfo("Beginning reauthentication flow due to login error.");
      this.setReauthenticatingAndLogin();
    } else if (this.didMsalInvalidateTheProfile()) {
      this.loggingService.logInfo("Beginning reauthentication flow due to absent user profile.");
      this.setReauthenticatingAndLogin();
    } else if (this.isProfileExpired(msalAuthenticationService.userInfo)) {
      this.loggingService.logInfo("Beginning reauthentication flow due to expired msal token / profile.");
      this.setReauthenticatingAndLogin();
    } else if (this.hasReauthenticatedAlready()) {
      // Victory: we have already reauthenticated
      windowCommunicationBridgeService
        .publish(reauthorizationCompletedEventName, {})
        .then(() => {
          this.$window.localStorage[reauthorizationInProgressLocalStorageKeyName] = false;
          window.close();
        })
        .then(() => {
          // Show the "close me" message at a delayed time to try to prevent most of the flashing we do otherwise.
          $timeout(
            () => {
              this.showFallbackMessage = true;
            },
            3000,
            true
          );
        });
    } else {
      if (closeIfAuthenticated) {
        // Somebody popped this window wanting to make sure we were logged in, and it seems like we are, so close it.
        window.close();
      } else {
        // Basically, we popped this guy thinking something needed to be fixed and haven't hit any of our other cases...
        //  something is wrong.
        this.loggingService.logError("Reauth window opened when there was no error");
        this.showFallbackMessage = true;
      }
    }
  }

  private hasReauthenticatedAlready(): boolean {
    return this.$location.search()[alreadyReauthenticatedQueryStringParameterName] == "true";
  }

  private setReauthenticatingAndLogin(): void {
    // Why does this code look insane?
    //  It's not! It turns out that $location updates need to be processed by a digest loop to show up in the browser nav bar,
    //   and parts of MSAL look at that nav bar to store redirect URI. We're setting the config redirect URI here (which updates
    //   the REQUEST but not the RESPONSE redirectUri, while waiting for the location change will ensure that everything is fully
    //   processed before invoking login).
    //
    // Eval = current digest loop
    // Apply = Next digest loop
    this.$rootScope.$evalAsync(() => {
      this.$window.localStorage[reauthorizationInProgressLocalStorageKeyName] = true;
      this.$location.search(alreadyReauthenticatedQueryStringParameterName, "true");
      this.$location.search(errorQueryStringParameterName, undefined);
      //this.msalAuthenticationService.config.redirectUri = this.$location.absUrl();
    });
    this.$rootScope.$applyAsync(() => {
      this.msalAuthenticationService.loginRedirect();
    });
  }

  private didMsalInvalidateTheProfile() {
    // If the token fails basic sanity checks in MSAL, this can happen.
    return !this.msalAuthenticationService.userInfo || !this.msalAuthenticationService.userInfo.idToken;
  }

  private isProfileExpired(userInfo: any): boolean {
    let expirationNumber = userInfo.idToken.exp;
    if (!Number.isFinite(expirationNumber)) {
      expirationNumber = 0;
    }
    return expirationNumber * 1000 < Date.now();
  }

  private isLoginRequired(): boolean {
    return (
      this.$location.search() && this.$location.search()[errorQueryStringParameterName] == msalErrorForLoginRequired
    );
  }

  public reauthorize(): void {
    this.setReauthenticatingAndLogin();
  }
}
